import { useState, useEffect } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { DoorOpen, Plus, Users, CheckCircle2, Clock, Hand, X } from "lucide-react";
import { fDate } from "@/lib/format";
import { toast } from "sonner";

const AUD = [{ v: "all", l: "Hela verksamheten" }, { v: "business_area", l: "Verksamhetsområde" }, { v: "department", l: "Avdelning(ar)" }, { v: "team", l: "Team" }, { v: "shift", l: "Skift" }];

function targetLabel(t, refs) {
  if (!t || t.type === "all") return "Hela verksamheten";
  const map = t.type === "business_area" ? refs.businessAreas : t.type === "department" ? refs.departments : t.type === "team" ? refs.teams : refs.shifts;
  const name = (map || []).find((x) => x.id === (t.ids || [])[0])?.name;
  return name ? name : "Riktad";
}

function CreateForm({ refs, onCreated }) {
  const [f, setF] = useState({ date: "", from_time: "", audienceType: "all", audienceId: "", note: "" });
  const [busy, setBusy] = useState(false);
  const opts = f.audienceType === "business_area" ? refs.businessAreas : f.audienceType === "department" ? refs.departments : f.audienceType === "team" ? refs.teams : f.audienceType === "shift" ? refs.shifts : [];
  const create = async () => {
    if (!f.date || !f.from_time) return toast.error("Ange datum och från-tid");
    const targets = f.audienceType === "all" ? { type: "all" } : { type: f.audienceType, ids: f.audienceId ? [f.audienceId] : [] };
    setBusy(true);
    try {
      await api.post("/early-leave", { date: f.date, from_time: f.from_time, targets, note: f.note });
      toast.success("Hemgångstillfälle skapat – berörda har notifierats");
      setF({ date: "", from_time: "", audienceType: "all", audienceId: "", note: "" });
      onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="mb-6 grid gap-3 p-4 lg:grid-cols-4">
      <div><Label>Datum</Label><Input type="date" value={f.date} onChange={(e) => setF((s) => ({ ...s, date: e.target.value }))} data-testid="el-date" /></div>
      <div><Label>Hemgång gäller från</Label><Input type="time" value={f.from_time} onChange={(e) => setF((s) => ({ ...s, from_time: e.target.value }))} data-testid="el-time" /></div>
      <div><Label>Gäller</Label>
        <Select value={f.audienceType} onValueChange={(v) => setF((s) => ({ ...s, audienceType: v, audienceId: "" }))}>
          <SelectTrigger data-testid="el-aud-type"><SelectValue /></SelectTrigger>
          <SelectContent>{AUD.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select></div>
      {f.audienceType !== "all"
        ? <div><Label>Välj del</Label>
            <Select value={f.audienceId} onValueChange={(v) => setF((s) => ({ ...s, audienceId: v }))}>
              <SelectTrigger data-testid="el-aud-target"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select></div>
        : <div className="hidden lg:block" />}
      <div className="lg:col-span-3"><Label>Notis (valfritt)</Label><Input value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} placeholder="t.ex. Lågt orderflöde i eftermiddag" data-testid="el-note" /></div>
      <div className="flex items-end"><Button onClick={create} disabled={busy} data-testid="el-create"><Plus className="mr-1 h-4 w-4" />Öppna hemgång</Button></div>
    </Card>
  );
}

function Interests({ oid, canConfirm, onDone }) {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await api.get(`/early-leave/${oid}/interests`); setItems(r.data); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const confirm = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length) return toast.error("Välj minst en person");
    setBusy(true);
    try { const r = await api.post(`/early-leave/${oid}/confirm`, { interest_ids: ids }); toast.success(`${r.data.confirmed} hemgång(ar) bekräftade`); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (items === null) return <p className="text-xs text-muted-foreground">Laddar anmälningar…</p>;
  if (items.length === 0) return <p className="text-xs text-muted-foreground">Inga anmälningar ännu.</p>;
  return (
    <div className="mt-2 space-y-1">
      {items.map((i) => (
        <div key={i.id} className="flex items-center gap-2 rounded-md border p-2 text-sm" data-testid={`interest-${i.id}`}>
          {canConfirm && i.status !== "bekräftad" &&
            <Checkbox checked={!!sel[i.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [i.id]: v }))} data-testid={`sel-${i.id}`} />}
          <span className="font-medium">{i.employee_name}</span>
          {i.status === "bekräftad"
            ? <Badge variant="secondary" className="ml-auto gap-1"><CheckCircle2 className="h-3 w-3" />Bekräftad</Badge>
            : <Badge variant="outline" className="ml-auto gap-1"><Hand className="h-3 w-3" />Intresse</Badge>}
        </div>
      ))}
      {canConfirm && <Button size="sm" className="mt-1" onClick={confirm} disabled={busy} data-testid={`confirm-${oid}`}><CheckCircle2 className="mr-1 h-4 w-4" />Bekräfta valda</Button>}
    </div>
  );
}

export default function TidigareHemgang() {
  const { data, reload } = useApi("/early-leave");
  const { data: teams } = useApi("/teams");
  const { data: departments } = useApi("/departments");
  const { data: shifts } = useApi("/shifts");
  const { data: businessAreas } = useApi("/business_areas");
  const refs = { teams, departments, shifts, businessAreas };
  const [openList, setOpenList] = useState({});

  const isMgr = data?.is_manager;
  const canConfirm = data?.can_confirm;
  const opps = data?.opportunities || [];

  const volunteer = async (oid) => { try { await api.post(`/early-leave/${oid}/interest`); toast.success("Intresse anmält"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const withdraw = async (oid) => { try { await api.delete(`/early-leave/${oid}/interest`); toast.success("Intresse borttaget"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const closeOpp = async (oid) => { try { await api.post(`/early-leave/${oid}/close`); reload(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Tidigare hemgång" icon={DoorOpen}
        subtitle={isMgr ? "Öppna frivillig tidigare hemgång för valda delar av verksamheten. Medarbetare anmäler intresse och du bekräftar innan schemat ändras."
          : "Frivillig tidigare hemgång. Anmäl intresse om du vill gå tidigare – din chef bekräftar."} />

      {isMgr && <CreateForm refs={refs} onCreated={reload} />}

      {opps.length === 0 ? <EmptyState icon={DoorOpen} title="Inga hemgångstillfällen" text={isMgr ? "Öppna ett tillfälle ovan." : "Just nu finns inga erbjudanden för dig."} /> :
        <div className="space-y-3">
          {opps.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`opp-${o.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <DoorOpen className="h-4 w-4 text-accent" />
                <span className="font-medium">{fDate(o.date)} · från kl {o.from_time}</span>
                <Badge variant="outline" className="gap-1"><Users className="h-3 w-3" />{targetLabel(o.targets, refs)}</Badge>
                <Badge variant={o.status === "öppen" ? "default" : "secondary"}>{o.status === "öppen" ? "Öppen" : "Stängd"}</Badge>
                {isMgr && <span className="ml-auto text-xs text-muted-foreground">{o.interest_count} anmälda · {o.confirmed_count} bekräftade</span>}
              </div>
              {o.note && <p className="mt-1 text-sm text-muted-foreground">{o.note}</p>}

              {!isMgr && (
                <div className="mt-3">
                  {o.my_interest === "bekräftad" ? <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" />Bekräftad – du kan gå kl {o.from_time}</Badge>
                    : o.my_interest === "intresse" ? <div className="flex items-center gap-2"><Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Väntar på chefens bekräftelse</Badge><Button size="sm" variant="ghost" onClick={() => withdraw(o.id)} data-testid={`withdraw-${o.id}`}><X className="mr-1 h-4 w-4" />Ångra</Button></div>
                    : o.status === "öppen" ? <Button size="sm" onClick={() => volunteer(o.id)} data-testid={`volunteer-${o.id}`}><Hand className="mr-1 h-4 w-4" />Anmäl intresse att gå</Button>
                    : <span className="text-xs text-muted-foreground">Stängd</span>}
                </div>
              )}

              {isMgr && (
                <div className="mt-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOpenList((s) => ({ ...s, [o.id]: !s[o.id] }))} data-testid={`show-interests-${o.id}`}>
                      {openList[o.id] ? "Dölj anmälningar" : `Visa anmälningar (${o.interest_count})`}
                    </Button>
                    {o.status === "öppen" && <Button size="sm" variant="ghost" onClick={() => closeOpp(o.id)} data-testid={`close-${o.id}`}>Stäng</Button>}
                  </div>
                  {openList[o.id] && <Interests key={`${o.id}-${o.interest_count}-${o.confirmed_count}`} oid={o.id} canConfirm={canConfirm} onDone={reload} />}
                </div>
              )}
            </Card>
          ))}
        </div>}
    </div>
  );
}
